import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, UserProfile } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, profileData?: {
    phone_number?: string;
    kakao_id?: string;
    telegram_id?: string;
  }) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 초기 세션 확인
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('세션 로드 오류:', error);
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        if (!session) {
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setUser(session.user);
        if (session.user) {
          await loadProfile(session.user.id);
        }
        setLoading(false);
      } catch (error) {
        console.error('인증 초기화 실패:', error);
        await supabase.auth.signOut();
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    initializeAuth();

    // 인증 상태 변경 감지
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user);
          loadProfile(session.user.id);
        }
        return;
      }

      if (event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        if (session?.user) {
          setLoading(false);
          await loadProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId: string) => {
    try {
      console.log('프로필 로딩 시작:', userId);
      
      // 프로필이 생성될 때까지 재시도
      let retries = 0;
      const maxRetries = 10;
      let profileData = null;

      while (retries < maxRetries && !profileData) {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          console.error('프로필 로드 오류:', error);
          if (retries === maxRetries - 1) {
            setProfile(null);
            return;
          }
        } else if (data) {
          profileData = data;
          console.log('프로필 로드 성공:', profileData);
          break;
        }

        retries++;
        if (retries < maxRetries) {
          console.log(`프로필 로드 재시도 ${retries}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (profileData) {
        setProfile(profileData);
        console.log('프로필 상태 업데이트 완료:', profileData.is_admin ? '관리자' : '일반 사용자');
      } else {
        console.warn('프로필을 찾을 수 없습니다');
        setProfile(null);
      }
    } catch (error) {
      console.error('프로필 처리 실패:', error);
      setProfile(null);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    profileData?: {
      phone_number?: string;
      kakao_id?: string;
      telegram_id?: string;
    }
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
      if (!data.user) throw new Error('회원가입에 실패했습니다');

      // 트리거가 프로필을 생성할 때까지 대기
      let retries = 0;
      const maxRetries = 10;
      
      while (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profile) {
          // 이메일과 추가 정보 업데이트
          await supabase
            .from('user_profiles')
            .update({
              email: email,
              phone_number: profileData?.phone_number || null,
              kakao_id: profileData?.kakao_id || null,
              telegram_id: profileData?.telegram_id || null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', data.user.id);
          break;
        }
        
        retries++;
      }

      return data;
    } catch (error: any) {
      throw new Error(error.message || '회원가입에 실패했습니다');
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      return data;
    } catch (error: any) {
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setProfile(null);
    } catch (error: any) {
      console.error('로그아웃 오류:', error);
      setUser(null);
      setProfile(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signUp, signIn, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
